import React, { useCallback, useRef, useEffect } from 'react';
import { connect } from 'react-redux';
import { Form } from 'antd';
import { get } from 'lodash';

import SchemaDrawer from 'APP_ROOT/components/form-viewer/object-types/object-field';
import { getSelectedForm, getTemplates } from 'APP_ROOT/selectors/form';
import changeFormTab from 'APP_ROOT/actions/change-form-tab';
import addRepeaterItemAction from 'APP_ROOT/actions/add-repeater-item';
import removeRepeaterItemAction from 'APP_ROOT/actions/remove-repeater-item';
import emitter, { EventTypes } from 'APP_ROOT/utils/eventEmitter';
import { reportFormName, reportFormOptions } from 'APP_ROOT/components/form-viewer/forms/utils/constants';

/**
 * Thin, standalone reimplementation of `forms/preview-form.js`.
 *
 * It reuses the real recursive renderer (`SchemaDrawer` = object-field) and the
 * exact `formProps` contract preview mode expects, but drops the report
 * chrome (FormHeader, ReviewPanel, share/reassign/delete actions, modals,
 * submit/autosave). The render path is:
 *
 *   connect(getSelectedForm, app, user, attachments)
 *     -> Form.create(reportFormOptions)   // injects the redux-form `form` prop
 *       -> <SchemaDrawer> (top navigation tabs) + <SchemaDrawer> (tab body)
 *
 * `isPreviewMode` / `isPreviewModeEnabled` are threaded through so the field
 * components keep all their network/autosave side effects switched off.
 */
function PreviewFormBody(props) {
  const {
    dispatch,
    form,
    selectedForm = {},
    user: { agencyId } = {},
    isCustomerReviewNote = false,
    templates = {},
  } = props;

  const tabsRefs = useRef([]);
  const saveTabRef = useCallback((ref, tab = 0) => {
    tabsRefs.current[tab] = ref;
  }, []);

  const syncTab = useCallback(
    active => {
      dispatch(changeFormTab(active));
    },
    [dispatch]
  );

  const noop = useCallback(() => {}, []);

  // Repeater add/remove buttons (and a few other controls) don't dispatch
  // directly — they emit through the form event bus, exactly as the real
  // report form does. Bridge the two events we care about for an interactive
  // preview: CALL_FORM_METHOD (addRepeaterItem/removeRepeaterItem) and
  // DISPATCH_IN_FORM (a thunk to dispatch). Everything else is a safe no-op.
  useEffect(() => {
    const onCallFormMethod = (name, formName, ...args) => {
      if (formName !== reportFormName) return;
      if (name === 'addRepeaterItem') {
        dispatch(addRepeaterItemAction(...args));
      } else if (name === 'removeRepeaterItem') {
        const [keys = [], index = 0] = args;
        keys.forEach(key => dispatch(removeRepeaterItemAction(key, index)));
      }
    };
    const onDispatchInForm = (formName, fn) => {
      if (formName !== reportFormName) return;
      if (typeof fn === 'function' || (fn && fn.type)) dispatch(fn);
    };
    emitter.on(EventTypes.CALL_FORM_METHOD, onCallFormMethod);
    emitter.on(EventTypes.DISPATCH_IN_FORM, onDispatchInForm);
    return () => {
      emitter.off(EventTypes.CALL_FORM_METHOD, onCallFormMethod);
      emitter.off(EventTypes.DISPATCH_IN_FORM, onDispatchInForm);
    };
  }, [dispatch]);

  const templateObj = get(selectedForm, 'template', {});
  const jsonSchema = get(templateObj, 'formSchema', {});
  const formSchema = get(jsonSchema, 'form', {});
  const { presentation = {}, meta = {}, tab = 0 } = selectedForm;
  const { overrides = {}, validations = {} } = jsonSchema;

  const settings = {
    syncTab,
    formSchema,
    validateFields: noop,
    validateFormFields: noop,
    saveTabRef,
    supportsDraft: false,
    formExist: false,
    templateType: get(selectedForm, ['template', 'type'], ''),
    agencyId,
    formName: reportFormName,
    isEditor: false,
    isEditMode: false,
  };

  const formProps = {
    presentation,
    form,
    overrides,
    settings,
    validations,
    tab,
    meta,
    onSubmit: noop,
    isReviewer: false,
    isViewer: false,
    canSubmit: false,
    loading: false,
    // Real template map from the store (state.form.templates), keyed by
    // templateType. BodyDiagram.getValidParts() reads
    // templates[settings.templateType].formSchema.enums[enumRef] to mark the
    // selectable body regions; an empty map left every injuries-body figure
    // rendered but non-interactive.
    templates,
    timezone: get(props, ['user', 'agency', 'timezone'], 'America/Chicago'),
  };

  // Nothing loaded yet (presentation transform still running): render nothing.
  if (!formSchema || !formSchema.type) return null;

  return (
    <Form className="bdm-form" hideRequiredMark onSubmit={e => e.preventDefault()}>
      <div className="header-info" data-mode="preview">
        <SchemaDrawer
          {...formProps}
          {...formSchema}
          tabsRefs={tabsRefs.current}
          isTopNavigation
          isPreviewMode
          isPreviewModeEnabled
          key="header"
          showNavigation
          options={{ showNavigation: true }}
        />
      </div>

      <SchemaDrawer
        {...formProps}
        {...formSchema}
        isPreviewModeEnabled
        isCustomerReviewNote={isCustomerReviewNote}
      />
    </Form>
  );
}

const mapState = state => ({
  selectedForm: getSelectedForm(state),
  user: state.session.currentUser,
  // Consumed by reportFormOptions.mapPropsToFields (Form.create), not by the
  // component body directly.
  attachments: state.attachments,
  // The seeded template map (state.form.templates), threaded down so
  // BodyDiagram can resolve valid body-part values for its figures.
  templates: getTemplates(state),
});

export default connect(mapState)(Form.create(reportFormOptions)(PreviewFormBody));
